import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  Activity,
  BookOpen,
  ChevronRight,
} from 'lucide-react'
import { EntityPageHeader } from '@/shared/components'
import { formateursAdminApi } from '../api/formateurs-admin.api'
import { etudiantsApi } from '@/features/etudiants/api/etudiants.api'
import { programmesApi } from '@/features/programmes/api/programmes.api'

function getInitials(prenom: string, nom: string) {
  return `${prenom?.[0] ?? ''}${nom?.[0] ?? ''}`.toUpperCase()
}

export function AdminDashboardPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data: formateurs = [] } = useQuery({
    queryKey: ['admin', 'formateurs'],
    queryFn: formateursAdminApi.getAll,
  })

  const { data: etudiants = [] } = useQuery({
    queryKey: ['admin', 'etudiants'],
    queryFn: etudiantsApi.getAll,
  })

  const { data: programmes = [] } = useQuery({
    queryKey: ['admin', 'programmes'],
    queryFn: programmesApi.getActifs,
  })

  const kpis = [
    {
      icon: Users,
      value: formateurs.length,
      label: t('admin.dashboard.kpi.formateurs', 'Formateurs actifs'),
      bgColor: 'bg-primary/10',
      iconColor: 'text-primary',
    },
    {
      icon: GraduationCap,
      value: etudiants.length,
      label: t('admin.dashboard.kpi.etudiants', 'Étudiants inscrits'),
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
    },
    {
      icon: Activity,
      value: '—',
      label: t('admin.dashboard.kpi.connexions', "Connexions aujourd'hui"),
      bgColor: 'bg-amber-50',
      iconColor: 'text-amber-600',
    },
    {
      icon: BookOpen,
      value: programmes.length,
      label: t('admin.dashboard.kpi.programmes', 'Programmes actifs'),
      bgColor: 'bg-violet-50',
      iconColor: 'text-violet-600',
    },
  ]

  return (
    <div className="space-y-8">
      <EntityPageHeader
        icon={LayoutDashboard}
        title={t('admin.dashboard.title', 'Tableau de bord')}
        subtitle={t('admin.dashboard.subtitle', "Vue d'ensemble de l'application")}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((kpi) => (
          <div
            key={kpi.label}
            className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm"
          >
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${kpi.bgColor}`}>
              <kpi.icon className={`h-5 w-5 ${kpi.iconColor}`} />
            </div>
            <p className="mt-3 text-2xl font-bold text-neutral-900">{kpi.value}</p>
            <p className="mt-0.5 text-xs text-neutral-500">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* 2-column layout */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Formateurs summary */}
        <div className="rounded-xl border border-neutral-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-neutral-100 px-6 py-4">
            <h3 className="text-sm font-semibold text-neutral-900">
              {t('admin.dashboard.formateurs', 'Formateurs')}
            </h3>
            <button
              onClick={() => navigate('/formateurs')}
              className="text-xs font-medium text-primary hover:text-primary/80"
            >
              {t('admin.dashboard.viewAll', 'Voir tout')}
            </button>
          </div>
          <div className="divide-y divide-neutral-100">
            {formateurs.length === 0 ? (
              <div className="px-6 py-8 text-center text-sm text-neutral-400">
                {t('admin.dashboard.noFormateurs', 'Aucun formateur')}
              </div>
            ) : (
              formateurs.slice(0, 5).map((f) => (
                <div key={f.id} className="flex items-center gap-3 px-6 py-3 hover:bg-neutral-50">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/60 text-xs font-bold text-white">
                    {getInitials(f.prenom, f.nom)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900">
                      {f.prenom} {f.nom}
                    </p>
                    <p className="text-xs text-neutral-400">{f.email}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-neutral-300" />
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent activity placeholder */}
        <div className="rounded-xl border border-neutral-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-neutral-100 px-6 py-4">
            <h3 className="text-sm font-semibold text-neutral-900">
              {t('admin.dashboard.recentActivity', 'Activité récente')}
            </h3>
            <button
              onClick={() => navigate('/logs')}
              className="text-xs font-medium text-primary hover:text-primary/80"
            >
              {t('admin.dashboard.viewAll', 'Voir tout')}
            </button>
          </div>
          <div className="px-6 py-8 text-center text-sm text-neutral-400">
            {t('admin.dashboard.noActivity', 'Le journal d\'activité sera disponible prochainement')}
          </div>
        </div>
      </div>
    </div>
  )
}
